import React, { forwardRef, useRef, useState } from 'react';

import { Input as NewUiInput } from '@/components/new-ui/input';
import { Input, type InputProps } from '@/components/ui/input';
import useClickOutside from '@/hooks/use-click-outside';
import { useDebouncedCallback } from '@/hooks/use-debounced-callback';

import { type SuggestionMapped, usePlaceSuggestions } from '../api/place-suggestions';

export type OnSelectSuggestion = (data: SuggestionMapped) => void;

interface AddressAutocompleteProps extends InputProps {
  onSelectSuggestion: OnSelectSuggestion;
  /** Opt-in: render with the redesign input and suggestion list. */
  newUi?: boolean;
  selectedValue?: string;
}

export const AddressAutocomplete: React.FC<AddressAutocompleteProps> = forwardRef<
  HTMLInputElement,
  AddressAutocompleteProps
>(({ onChange, onSelectSuggestion, newUi = false, selectedValue, value, ...inputProps }, ref) => {
  const [searchTerm, setSearchTerm] = useState('');

  const { data } = usePlaceSuggestions(searchTerm);
  const suggestions = data ?? [];

  const prevSelectedValue = useRef('');

  const clearSuggestions = () => setSearchTerm('');

  const debouncedSetSearchTerm = useDebouncedCallback(setSearchTerm, 500);

  const containerRef = useClickOutside<HTMLDivElement>(clearSuggestions, !!suggestions.length);

  const handleSuggestionClick = (data: SuggestionMapped) => () => {
    prevSelectedValue.current = data.address ?? data.place_name;
    clearSuggestions();
    return onSelectSuggestion(data);
  };

  const handleInputChange: InputProps['onChange'] = (event) => {
    if (event.target.value !== prevSelectedValue.current) {
      prevSelectedValue.current = '';
    }

    onChange?.(event);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: ignored
  React.useEffect(() => {
    const currentValue = value?.toString();
    const currentSelectedValue = selectedValue ?? prevSelectedValue.current;

    if (currentValue && currentValue !== currentSelectedValue) {
      debouncedSetSearchTerm(currentValue);
    }
  }, [selectedValue, value]);

  React.useEffect(() => {
    if (selectedValue) {
      prevSelectedValue.current = selectedValue;
      debouncedSetSearchTerm.cancel();
      setSearchTerm('');
    }
  }, [debouncedSetSearchTerm, selectedValue]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: ignored
  React.useLayoutEffect(() => {
    if (value) {
      prevSelectedValue.current = value?.toString();
    }
  }, []);

  // The redesign input is a plain input: drop the props only the legacy input understands
  const {
    wrapperClassName: _wrapperClassName,
    label: _label,
    errors: _errors,
    startIcon: _startIcon,
    endIcon: _endIcon,
    size: _size,
    variant: _variant,
    color: _color,
    ...newUiInputProps
  } = inputProps;

  return (
    <div className="relative w-full">
      {newUi ? (
        <NewUiInput {...newUiInputProps} ref={ref} value={value} onChange={handleInputChange} />
      ) : (
        <Input {...inputProps} ref={ref} value={value} onChange={handleInputChange} />
      )}
      {suggestions.length > 0 && (
        <div
          ref={containerRef}
          className={
            newUi
              ? 'new-ui absolute z-50 mt-1 flex max-h-60 w-full flex-col overflow-y-auto rounded-md border bg-popover p-1 text-popover-foreground shadow-md'
              : 'absolute z-50 mt-2 flex max-h-80 w-full flex-col gap-1 overflow-y-scroll rounded-md border border-gray-200 bg-white p-2 shadow-md'
          }
        >
          {suggestions.map((suggestion) => (
            <button
              key={suggestion.id}
              type="button"
              className={
                newUi
                  ? 'relative flex w-full cursor-default select-none items-center rounded-sm px-2 py-1.5 text-left text-sm outline-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground'
                  : 'relative flex w-full cursor-pointer select-none items-center gap-2 rounded-sm border border-transparent px-2 py-1.5 text-md text-primary outline-none transition-colors hover:border-primary-light data-[disabled]:pointer-events-none data-[disabled]:opacity-50'
              }
              onClick={handleSuggestionClick(suggestion)}
            >
              {suggestion.place_name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
});
